import { useEffect, useRef } from 'react';
import { atomFamily, useRecoilCallback } from 'recoil';
import { Organization, OrganizationSummary } from 'services/api';

export const isOrganizationFollowedState = atomFamily({
  key: 'OrganizationsFollowed',
  default: false as boolean,
});

// Track which organizations have been locally modified by user action
// This prevents API data from overwriting user-initiated changes
const userModifiedOrgs = new Set<string>();

export const markOrganizationAsUserModified = (orgId: string): void => {
  userModifiedOrgs.add(orgId);
  // Clear the flag after a short delay to allow API data to sync after server confirms
  setTimeout(() => userModifiedOrgs.delete(orgId), 5000);
};

/**
 * Optimistically set follow state after a local action (follow, unfollow, or
 * auto-accept join, which creates a follower). Marks the org as user-modified
 * so a stale list payload cannot overwrite the new value.
 */
export const useMarkOrganizationFollowed = (): ((
  orgId: string,
  followed: boolean,
) => void) => {
  return useRecoilCallback(
    ({ set }) =>
      (orgId: string, followed: boolean) => {
        markOrganizationAsUserModified(orgId);
        set(isOrganizationFollowedState(orgId), followed);
      },
    [],
  );
};

export const useSetOrganizationFollowedState = (
  organizations?:
    | Organization[]
    | Organization
    | OrganizationSummary[]
    | OrganizationSummary,
): void => {
  // Track which org IDs we've already initialized to prevent re-running on same data
  const initializedOrgsRef = useRef<Set<string>>(new Set());

  const setOrganizationFollowedState = useRecoilCallback(
    ({ set, snapshot }) => async (organizations: (Organization | OrganizationSummary)[]) => {
      for (const { id, isFollowed } of organizations) {
        if (isFollowed !== undefined) {
          const currentValue = await snapshot.getPromise(isOrganizationFollowedState(id));
          const wasInitialized = initializedOrgsRef.current.has(id);
          const isUserModified = userModifiedOrgs.has(id);

          // Skip if user has locally modified this org (prevents overwriting user action)
          if (isUserModified)
            continue;


          // Only set from API if:
          // 1. This org hasn't been initialized yet, OR
          // 2. The API value matches current value (no conflict), OR
          // 3. API says true (user followed elsewhere, we should sync)
          if (!wasInitialized || currentValue === isFollowed || isFollowed === true) {
            set(isOrganizationFollowedState(id), isFollowed);
            initializedOrgsRef.current.add(id);
          }
        }
      }
    },
  );

  useEffect(() => {
    if (Array.isArray(organizations))
      setOrganizationFollowedState(organizations);
    else if (organizations) setOrganizationFollowedState([organizations]);
  }, [organizations]);
};
